import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, MapPin, Check, ArrowRight, Search, ShieldCheck, Sparkles } from 'lucide-react';
import { useMarket } from '../context/MarketContext';
import { 
    GEO_STORAGE_KEY, 
    MANUAL_LOCK_KEY,
    COUNTRY_DEFAULT_MAPPINGS,
    SUPPORTED_LANGUAGES,
    isSupportedLanguage,
    getBestSupportedLanguageForCountry,
    detectAndApplyRegionalSettings
} from '../utils/countryLanguageEngine';
import { parseLanguagesJson } from '../context/MarketContext';

const GlobalMarketSelector = ({ autoForward = true }) => {
    const navigate = useNavigate();
    const { activeCountries } = useMarket();

    const [detectedCountryCode, setDetectedCountryCode] = useState('SA');
    const [detectedLangCode, setDetectedLangCode] = useState('ar');
    const [detectedCurrCode, setDetectedCurrCode] = useState('SAR');

    const [selectedCountryCode, setSelectedCountryCode] = useState('SA');
    const [selectedLangCode, setSelectedLangCode] = useState('ar');
    const [selectedCurrCode, setSelectedCurrCode] = useState('SAR');

    const [searchQuery, setSearchQuery] = useState('');
    const [detecting, setDetecting] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const checkExistingOrDetect = async () => {
            // 1. Check if user already has a saved manual choice or stored preference
            try {
                const savedStr = localStorage.getItem(GEO_STORAGE_KEY);
                if (savedStr) {
                    const parsed = JSON.parse(savedStr);
                    if (parsed && parsed.country && parsed.language) {
                        let targetCountry = parsed.country.toLowerCase();
                        let targetLang = parsed.language.toLowerCase();
                        if (!isSupportedLanguage(targetLang)) {
                            targetLang = getBestSupportedLanguageForCountry(targetCountry);
                        }
                        if (autoForward) {
                            navigate(`/${targetCountry}/${targetLang}/`, { replace: true });
                            return;
                        }
                    }
                }
            } catch (_) {}

            // 2. Perform live network detection
            try {
                const detected = await detectAndApplyRegionalSettings(false);
                let country = (detected?.country || 'SA').toUpperCase();
                let lang = detected?.language || 'ar';
                if (!isSupportedLanguage(lang)) {
                    lang = getBestSupportedLanguageForCountry(country);
                }
                let curr = detected?.currency || 'SAR';

                if (isMounted) {
                    setDetectedCountryCode(country);
                    setDetectedLangCode(lang);
                    setDetectedCurrCode(curr);

                    setSelectedCountryCode(country);
                    setSelectedLangCode(lang);
                    setSelectedCurrCode(curr);
                }

                // If autoForward is enabled (root visitor), enter market directly
                if (autoForward) {
                    navigate(`/${country.toLowerCase()}/${lang.toLowerCase()}/`, { replace: true });
                    return;
                }
            } catch (err) {
                console.warn('Market detection fallback:', err);
                if (autoForward) {
                    navigate('/sa/ar/', { replace: true });
                    return;
                }
            } finally {
                if (isMounted) setDetecting(false);
            }
        };

        checkExistingOrDetect();
        return () => { isMounted = false; };
    }, [navigate, autoForward]);

    const detectedCountry = useMemo(() => {
        return activeCountries.find(c => c.countryCode?.toUpperCase() === detectedCountryCode) || activeCountries[0];
    }, [activeCountries, detectedCountryCode]);

    const selectedCountry = useMemo(() => {
        return activeCountries.find(c => c.countryCode?.toUpperCase() === selectedCountryCode) || activeCountries[0];
    }, [activeCountries, selectedCountryCode]);

    // Available languages for the chosen country
    const availableLanguages = useMemo(() => {
        return SUPPORTED_LANGUAGES;
    }, []);

    const filteredCountries = useMemo(() => {
        if (!searchQuery.trim()) return activeCountries;
        const q = searchQuery.toLowerCase().trim();
        return activeCountries.filter(c => 
            (c.countryName && c.countryName.toLowerCase().includes(q)) ||
            (c.nativeName && c.nativeName.toLowerCase().includes(q)) ||
            (c.countryCode && c.countryCode.toLowerCase().includes(q))
        );
    }, [activeCountries, searchQuery]);

    const handleSelectCountry = (country) => {
        setSelectedCountryCode(country.countryCode);
        const mapping = COUNTRY_DEFAULT_MAPPINGS[country.countryCode];
        let defaultLang = country.defaultLanguage || mapping?.lang || getBestSupportedLanguageForCountry(country.countryCode);
        if (!isSupportedLanguage(defaultLang)) {
            defaultLang = getBestSupportedLanguageForCountry(country.countryCode);
        }
        const defaultCurr = country.defaultCurrency || mapping?.curr || 'USD';
        setSelectedLangCode(defaultLang);
        setSelectedCurrCode(defaultCurr);
    };

    const handleEnterMarket = (cCode, lCode, currCode) => {
        const country = (cCode || selectedCountryCode).toLowerCase();
        let language = (lCode || selectedLangCode).toLowerCase();
        if (!isSupportedLanguage(language)) {
            language = getBestSupportedLanguageForCountry(country);
        }
        const currency = (currCode || selectedCurrCode).toUpperCase();

        try {
            localStorage.setItem(MANUAL_LOCK_KEY, 'true');
            localStorage.setItem('lux_country', country.toUpperCase());
            localStorage.setItem('luxvono_user_explicit_language', language);
            localStorage.setItem('luxvono_user_currency', currency);
            localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify({
                country: country.toUpperCase(),
                language,
                currency,
                userExplicitLanguage: true
            }));
        } catch (_) {}

        navigate(`/${country}/${language}/`);
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'radial-gradient(ellipse at 50% 20%, #151c2e 0%, #080c14 100%)',
            color: '#f8fafc',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 16px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            {/* Top Brand Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{
                    fontSize: '32px',
                    fontWeight: 900,
                    letterSpacing: '3px',
                    background: 'linear-gradient(135deg, #FFF 0%, #FFFFFF 50%, #E2E8F0 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '8px'
                }}>
                    LUXVONO
                </div>
                <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
                    The Sovereign Luxury Marketplace • Choose Your Destination
                </p>
            </div>

            {/* Main Selection Card */}
            <div style={{
                width: '100%',
                maxWidth: '680px',
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '24px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(16px)',
                overflow: 'hidden'
            }}>
                {/* Detected Suggestion Banner */}
                {detectedCountry && (
                    <div style={{
                        padding: '20px 24px',
                        background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <span style={{ fontSize: '32px' }}>{detectedCountry.flagEmoji || '🌍'}</span>
                            <div>
                                <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#FFFFFF', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Sparkles size={13} />
                                    Detected Location / موقعك المكتشف
                                </div>
                                <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>
                                    {detectedCountry.nativeName ? `${detectedCountry.nativeName} (${detectedCountry.countryName})` : detectedCountry.countryName}
                                </div>
                                <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                                    Default: {detectedLangCode.toUpperCase()} • Currency: {detectedCurrCode}
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => handleEnterMarket(detectedCountryCode, detectedLangCode, detectedCurrCode)}
                            style={{
                                background: '#000000',
                                color: '#ffffff',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '12px',
                                padding: '10px 20px',
                                fontSize: '13.5px',
                                fontWeight: 800,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 4px 14px rgba(255, 255, 255, 0.3)'
                            }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            <span>Continue to Store</span>
                            <ArrowRight size={16} />
                        </button>
                    </div>
                )}

                {/* Country Search and Custom Selector */}
                <div style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <label style={{ fontSize: '13px', fontWeight: 700, color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <MapPin size={15} color="#FFFFFF" />
                            Select Country / اختر الدولة
                        </label>
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>{activeCountries.length} Global Markets</span>
                    </div>

                    {/* Search bar */}
                    <div style={{
                        position: 'relative',
                        marginBottom: '16px'
                    }}>
                        <Search size={16} color="#64748b" style={{ position: 'absolute', left: '14px', top: '13px' }} />
                        <input
                            type="text"
                            placeholder="Search countries by name or code (e.g. United States, Germany, الإمارات)..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: '10px 14px 10px 40px',
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                background: 'rgba(0, 0, 0, 0.3)',
                                color: '#fff',
                                fontSize: '13.5px',
                                outline: 'none'
                            }}
                        />
                    </div>

                    {/* Country Pills / Grid */}
                    <div style={{
                        maxHeight: '220px',
                        overflowY: 'auto',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '8px',
                        paddingRight: '4px',
                        marginBottom: '20px'
                    }}>
                        {filteredCountries.map(c => {
                            const isSelected = selectedCountryCode === c.countryCode;
                            return (
                                <div
                                    key={c.countryCode}
                                    onClick={() => handleSelectCountry(c)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 12px',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        border: isSelected ? '1px solid #FFFFFF' : '1px solid rgba(255, 255, 255, 0.06)',
                                        background: isSelected ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.02)',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <span style={{ fontSize: '20px' }}>{c.flagEmoji || '🌍'}</span>
                                    <div style={{ overflow: 'hidden' }}>
                                        <div style={{ fontSize: '13px', fontWeight: isSelected ? 700 : 500, color: isSelected ? '#FFFFFF' : '#f1f5f9', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                            {c.countryName}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                                            {c.countryCode} • {c.defaultCurrency}
                                        </div>
                                    </div>
                                    {isSelected && <Check size={16} color="#FFFFFF" style={{ marginLeft: 'auto' }} />}
                                </div>
                            );
                        })}
                    </div>

                    {/* Language & Currency Selection Row */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.08)'
                    }}>
                        <div>
                            <label style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                <Globe size={13} color="#FFFFFF" />
                                Language / اللغة
                            </label>
                            <select
                                value={selectedLangCode}
                                onChange={e => setSelectedLangCode(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: '12px',
                                    background: 'rgba(0, 0, 0, 0.4)',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    color: '#fff',
                                    fontSize: '13.5px',
                                    outline: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                {availableLanguages.map(l => (
                                    <option key={l.code} value={l.code}>
                                        {l.flag} {l.nativeName} ({l.name})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                <span>💵</span>
                                Currency / العملة
                            </label>
                            <select
                                value={selectedCurrCode}
                                onChange={e => setSelectedCurrCode(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: '12px',
                                    background: 'rgba(0, 0, 0, 0.4)',
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    color: '#fff',
                                    fontSize: '13.5px',
                                    outline: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                {[selectedCountry?.defaultCurrency || 'USD', 'USD', 'EUR', 'GBP', 'SAR', 'AED', 'CAD', 'CHF', 'KWD', 'QAR', 'BHD', 'OMR', 'TRY', 'CNY', 'JPY'].filter((v, i, a) => a.indexOf(v) === i).map(curr => (
                                    <option key={curr} value={curr}>{curr}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div style={{ marginTop: '24px' }}>
                        <button
                            type="button"
                            onClick={() => handleEnterMarket(selectedCountryCode, selectedLangCode, selectedCurrCode)}
                            style={{
                                width: '100%',
                                padding: '13px',
                                borderRadius: '14px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #FFFFFF 0%, #E2E8F0 100%)',
                                color: '#090d16',
                                fontSize: '15px',
                                fontWeight: 800,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 8px 24px rgba(255, 255, 255, 0.3)'
                            }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            <span>Enter Marketplace / دخول المنصة</span>
                            <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer trust badge */}
            <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '12px' }}>
                <ShieldCheck size={15} color="#FFFFFF" />
                <span>Sovereign Global Delivery • Secured Protocol</span>
            </div>
        </div>
    );
};

export default GlobalMarketSelector;
